import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, apiRequest } from '../api/client.ts';
import { useSession, offlineAuthorizationFor } from '../state/session.tsx';
import { Button, EmptyState, PageHeader, TextArea } from '../components/ui.tsx';
import { STAFF_PHOTO_MAX_BYTES, STAFF_PHOTO_MAX_COUNT, staffPhotoCopy } from '../../shared/staff-photos.ts';

type DraftPhoto = { clientId: string; file: File; receiptId?: string };
type Snapshot = { id: string; body: string; photos: DraftPhoto[] };
type PhotoPost = { id: string; body: string; name: string | null; photos: { id: string }[] };
type Copy = (typeof staffPhotoCopy)['en'] | (typeof staffPhotoCopy)['es'];

export default function StaffPhotosPage() {
  const s = useSession(),
    { i18n } = useTranslation();
  const copy = staffPhotoCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  if (
    !s.user ||
    !s.company ||
    !s.currentLocation ||
    s.company.accessLocked ||
    !s.can('staff_portal:read') ||
    !s.company.planFeatures?.includes('staff_portal')
  )
    return <p role="status">{copy.blocked}</p>;
  return (
    <Content
      key={JSON.stringify([
        s.user.companyId,
        s.user.id,
        s.currentLocation.id,
        offlineAuthorizationFor(s.user, s.company),
      ])}
      canWrite={s.can('staff_portal:write')}
    />
  );
}
function Preview({ file, copy }: { file: File; copy: Copy }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const current = URL.createObjectURL(file);
    setUrl(current);
    return () => URL.revokeObjectURL(current);
  }, [file]);
  return url ? (
    <img
      src={url}
      alt={copy.choose}
      className="h-40 w-full rounded-md bg-[color:var(--color-surface)] object-contain"
    />
  ) : null;
}
function PublishedPhoto({ id, copy }: { id: string; copy: Copy }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <div className="space-y-3 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4">
      <p role="status" className="text-sm text-[color:var(--color-muted)]">
        {copy.unavailable}
      </p>
      <Button onClick={() => setFailed(false)}>{copy.refresh}</Button>
    </div>
  ) : (
    <img
      src={`/api/v1/staff/photos/${id}`}
      alt={copy.title}
      className="max-h-80 w-full rounded-md bg-[color:var(--color-surface)] object-contain"
      onError={() => setFailed(true)}
    />
  );
}
export function StaffPhotoView({ id }: { id: string }) {
  const { i18n } = useTranslation();
  return <PublishedPhoto id={id} copy={staffPhotoCopy[i18n.language.startsWith('es') ? 'es' : 'en']} />;
}
function Content({ canWrite }: { canWrite: boolean }) {
  const { i18n } = useTranslation(),
    copy = staffPhotoCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const photoInput = useRef<HTMLInputElement>(null),
    photoLimitsId = useId();
  const [body, setBody] = useState(''),
    [photos, setPhotos] = useState<DraftPhoto[]>([]);
  const [posts, setPosts] = useState<PhotoPost[]>([]),
    [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false),
    [message, setMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0),
    [cursor, setCursor] = useState<string | null>(null);
  const [serverCanWrite, setServerCanWrite] = useState(false);
  const [drafts, setDrafts] = useState<{ id: string; sizeBytes: number }[]>([]);
  const [denied, setDenied] = useState(false);
  const alive = useRef(true),
    lock = useRef(false),
    pending = useRef<Snapshot | null>(null),
    readEpoch = useRef(0);
  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
      readEpoch.current++;
      pending.current = null;
    };
  }, []);
  function clearAccess(message: string) {
    pending.current = null;
    setPosts([]);
    setDrafts([]);
    setPhotos([]);
    setBody('');
    setCursor(null);
    setUncertain(false);
    setServerCanWrite(false);
    setDenied(true);
    setMessage(message);
  }
  async function refresh(before?: string) {
    if (lock.current) return;
    const epoch = ++readEpoch.current;
    try {
      const capability = await api.get<{ canWrite: boolean }>('/staff/photo-capability');
      if (!alive.current || epoch !== readEpoch.current) return;
      const data = await api.get<{ items: PhotoPost[]; nextCursor: string | null }>(
        `/staff/photo-posts${before ? `?before=${encodeURIComponent(before)}` : ''}`,
      );
      if (!alive.current || epoch !== readEpoch.current) return;
      const unused = await api.get<{ items: { id: string; sizeBytes: number }[] }>('/staff/photo-drafts');
      if (!alive.current || epoch !== readEpoch.current) return;
      setDrafts(unused.items);
      setDenied(false);
      if (!capability.canWrite) {
        pending.current = null;
        setPhotos([]);
        setBody('');
        setUncertain(false);
      }
      setEnabled(true);
      setServerCanWrite(capability.canWrite);
      setPosts((old) => (before ? [...old, ...data.items] : data.items));
      setCursor(data.nextCursor);
      setRefreshKey((old) => old + 1);
      setMessage('');
    } catch (error) {
      if (!alive.current || epoch !== readEpoch.current) return;
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 402 || status === 403) {
        clearAccess(copy.blocked);
      } else if (status === 404) {
        setEnabled(false);
        clearAccess(copy.disabled);
      } else setMessage(copy.error);
    }
  }
  async function publish() {
    if (lock.current) return;
    const snapshot = pending.current ?? { id: crypto.randomUUID(), body, photos: photos.map((p) => ({ ...p })) };
    pending.current = snapshot;
    lock.current = true;
    setBusy(true);
    setMessage('');
    // Retire outstanding reads so a stale list cannot overwrite the post-write list.
    readEpoch.current++;
    try {
      for (const photo of snapshot.photos) {
        if (!photo.receiptId) {
          const form = new FormData();
          form.append('file', photo.file);
          form.append('clientUploadId', photo.clientId);
          const receipt = await apiRequest<{ id: string }>('/staff/photos', { method: 'POST', formData: form });
          if (!alive.current) return;
          photo.receiptId = receipt.id;
          setPhotos((old) =>
            old.map((item) => (item.clientId === photo.clientId ? { ...item, receiptId: receipt.id } : item)),
          );
        }
      }
      if (!alive.current) return;
      await api.post('/staff/photo-posts', {
        clientMutationId: snapshot.id,
        body: snapshot.body,
        photoIds: snapshot.photos.map((p) => p.receiptId!),
      });
      if (!alive.current) return;
      pending.current = null;
      setUncertain(false);
      setBody('');
      setPhotos([]);
      setMessage(copy.saved);
      lock.current = false;
      await refresh();
      if (alive.current) setMessage(copy.saved);
    } catch (error) {
      if (!alive.current) return;
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 402 || status === 403) clearAccess(copy.blocked);
      else if (status && status >= 400 && status < 500 && status !== 408) {
        pending.current = null;
        setUncertain(false);
        setMessage(status === 404 ? copy.missing : copy.error);
      } else {
        setUncertain(true);
        setMessage(copy.uncertain);
      }
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function discard(id: string, localId?: string) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    readEpoch.current++;
    try {
      await api.delete(`/staff/photos/${encodeURIComponent(id)}`);
      if (!alive.current) return;
      setDrafts((old) => old.filter((p) => p.id !== id));
      if (localId) setPhotos((old) => old.filter((p) => p.clientId !== localId));
      setMessage(copy.discarded);
    } catch (error) {
      if (!alive.current) return;
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 402 || status === 403) clearAccess(copy.blocked);
      else if (status === 404) {
        setDrafts((old) => old.filter((p) => p.id !== id));
        if (localId) setPhotos((old) => old.filter((p) => p.clientId !== localId));
      } else setMessage(copy.error);
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const frozen = busy || uncertain;
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/staff"
        className="inline-flex min-h-11 items-center text-sm font-semibold text-[color:var(--color-brand)] hover:underline"
      >
        {copy.back}
      </Link>
      <PageHeader
        title={copy.title}
        description={copy.reading}
        actions={
          <Button disabled={frozen} onClick={() => void refresh()}>
            {copy.refresh}
          </Button>
        }
      />
      {message && (
        <p
          role="status"
          className="rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 text-sm"
        >
          {message}
        </p>
      )}
      {enabled === null && !denied && <p className="text-[color:var(--color-muted)]">{copy.loading}</p>}
      {enabled === false && !message && <p role="status">{copy.disabled}</p>}
      {enabled && !denied && canWrite && serverCanWrite && (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            void publish();
          }}
        >
          <TextArea
            label={copy.message}
            value={body}
            maxLength={4000}
            rows={4}
            disabled={frozen}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4">
            <input
              ref={photoInput}
              type="file"
              aria-label={copy.choose}
              aria-describedby={photoLimitsId}
              accept="image/jpeg,image/png"
              multiple
              disabled={frozen}
              hidden
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (
                  photos.length + files.length > STAFF_PHOTO_MAX_COUNT ||
                  files.some(
                    (file) =>
                      !['image/jpeg', 'image/png'].includes(file.type) ||
                      !file.size ||
                      file.size > STAFF_PHOTO_MAX_BYTES,
                  )
                ) {
                  setMessage(copy.invalid);
                  return;
                }
                setPhotos((old) => [...old, ...files.map((file) => ({ clientId: crypto.randomUUID(), file }))]);
                setMessage('');
              }}
            />
            <Button disabled={frozen} aria-describedby={photoLimitsId} onClick={() => photoInput.current?.click()}>
              {copy.choose}
            </Button>
            <p id={photoLimitsId} className="mt-3 text-sm text-[color:var(--color-muted)]">
              {copy.limits}
            </p>
          </div>
          {photos.length > 0 && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {photos.map((photo) => (
                <figure
                  key={photo.clientId}
                  className="min-w-0 space-y-3 rounded-md border border-[color:var(--color-line)] p-3"
                >
                  <Preview file={photo.file} copy={copy} />
                  <figcaption className="overflow-x-auto text-sm text-[color:var(--color-muted)]">
                    {photo.file.name}
                  </figcaption>
                  <Button
                    className="w-full"
                    disabled={frozen}
                    onClick={() =>
                      photo.receiptId
                        ? void discard(photo.receiptId, photo.clientId)
                        : setPhotos((old) => old.filter((p) => p.clientId !== photo.clientId))
                    }
                  >
                    {copy.remove}
                  </Button>
                </figure>
              ))}
            </div>
          )}
          <div className="mt-5 border-t border-[color:var(--color-line)] pt-4">
            <Button
              type="submit"
              variant="primary"
              className="w-full sm:w-auto"
              busy={busy}
              disabled={frozen || !body.trim() || !photos.length}
            >
              {copy.publish}
            </Button>
          </div>
        </form>
      )}
      {uncertain && (
        <Button variant="primary" busy={busy} className="w-full sm:w-auto" onClick={() => void publish()}>
          {copy.retry}
        </Button>
      )}
      {enabled && !denied && !posts.length && <EmptyState title={copy.empty} />}
      {enabled &&
        !denied &&
        canWrite &&
        serverCanWrite &&
        drafts.some((d) => !photos.some((p) => p.receiptId === d.id)) && (
          <section className="card space-y-3">
            <h2 className="text-lg font-semibold">{copy.unused}</h2>
            {drafts
              .filter((d) => !photos.some((p) => p.receiptId === d.id))
              .map((d) => (
                <Button key={d.id} className="w-full sm:w-auto" disabled={frozen} onClick={() => void discard(d.id)}>
                  {copy.discard} · {Math.ceil(d.sizeBytes / 1024)} KB
                </Button>
              ))}
          </section>
        )}
      {posts.map((post) => (
        <article key={post.id} className="card space-y-4">
          <p className="font-semibold">{post.name ?? copy.former}</p>
          <p className="whitespace-pre-wrap break-words">{post.body}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {post.photos.map((photo) => (
              <PublishedPhoto key={`${photo.id}:${refreshKey}`} id={photo.id} copy={copy} />
            ))}
          </div>
          <Link
            to="/staff"
            className="inline-flex min-h-11 items-center text-sm font-semibold text-[color:var(--color-brand)] hover:underline"
          >
            {copy.back}
          </Link>
        </article>
      ))}
      {cursor && (
        <Button disabled={frozen} onClick={() => void refresh(cursor)}>
          {copy.more}
        </Button>
      )}
    </div>
  );
}
